import { tapApi } from "../api";

/**
 * Polls job status and reports progress via callbacks.
 * Stops polling when job completes, fails, or is cancelled.
 * @param {string|number} jobId - Job ID to track
 * @param {Function} onStatusMessage - Called with (message, status) on updates
 * @param {Function} [onJobTasksLoaded] - (tasks, jobInfo) when job finishes; jobInfo has
 *   `jobStatus`, `errorCount`. Consumers can skip opening a UI (e.g. upload tasks drawer)
 *   when `jobStatus === "COMPLETE"` and `errorCount === 0`.
 */
export function startJobStatusTracker(
  jobId,
  onStatusMessage,
  onJobTasksLoaded,
) {
  let stopped = false;
  let lastMessage = "";

  const poll = () => {
    tapApi
      .jobs(jobId)
      .then((jobStatus) => {
        const data = jobStatus?.data;
        if (!data) {
          const message = `Job ${jobId}: Unable to fetch status.`;
          if (message !== lastMessage) {
            onStatusMessage(message, "error");
            lastMessage = message;
          }
          if (!stopped) setTimeout(poll, 2000);
          return;
        }

        let message = "";
        let status = "info";

        /** Statuses that indicate the job is still processing and polling should continue */
        const PENDING_STATUSES = [
          "CREATED",
          "RUNNING",
          "STARTING",
          "RETRYING",
          "QUEUED",
          "QUEUED_ADMINISTRATOR",
          "QUEUED_UPDATED",
          "QUEUED_UPDATED_USER_INITIATED",
          "QUEUED_UPDATED_ADMINISTRATOR",
        ];

        if (data.jobStatus === "CREATED") {
          message = `Job ${data.id} is creating tasks and preparing to start.`;
        } else if (
          data.jobStatus === "STARTING" ||
          data.jobStatus === "RETRYING" ||
          data.jobStatus === "QUEUED" ||
          data.jobStatus === "QUEUED_ADMINISTRATOR" ||
          data.jobStatus === "QUEUED_UPDATED" ||
          data.jobStatus === "QUEUED_UPDATED_USER_INITIATED" ||
          data.jobStatus === "QUEUED_UPDATED_ADMINISTRATOR"
        ) {
          message = `Job ${data.id} is ${data.jobStatus.toLowerCase().replace(/_/g, " ")}...`;
        } else if (data.jobStatus === "RUNNING") {
          message = `Job ${data.id} progress: ${data.progressCount} out of ${data.targetCount} tasks complete with ${data.errorCount} errors.`;
        } else if (data.jobStatus === "COMPLETE" && data.errorCount > 0) {
          message = `Job ${data.id} upload completed with errors. ${data.errorCount} error${data.errorCount === 1 ? " found" : "s found"}.`;
          status = "error";
          stopped = true;
        } else if (data.jobStatus === "COMPLETE") {
          message = `Job ${data.id} completed successfully!`;
          status = "success";
          stopped = true;
        } else if (data.jobStatus === "FAILED") {
          message = `Job ${data.id} failed!`;
          status = "error";
          stopped = true;
        } else if (data.jobStatus === "CANCELLED") {
          message = `Job ${data.id} was cancelled.`;
          status = "warning";
          stopped = true;
        } else {
          message = `Job ${data.id} is in an unknown state: ${data.jobStatus}`;
          status = "error";
          stopped = true;
        }

        if (message && message !== lastMessage) {
          onStatusMessage(message, status);
          lastMessage = message;
        }

        // If jobStatus is not a pending status, stop polling and fetch job tasks
        if (!PENDING_STATUSES.includes(data.jobStatus)) {
          stopped = true;
          const jobInfo = {
            jobStatus: data.jobStatus,
            errorCount: data.errorCount ?? 0,
          };
          tapApi.rateCardJobTasks(jobId).then((tasks) => {
            if (typeof onJobTasksLoaded === "function") {
              onJobTasksLoaded(tasks?.data?.tasks || [], jobInfo);
            }
          });
          return;
        }

        if (!stopped) setTimeout(poll, 2000);
      })
      .catch(() => {
        const message = `Job ${jobId}: Error fetching status.`;
        if (message !== lastMessage) {
          onStatusMessage(message, "error");
          lastMessage = message;
        }
        stopped = true;
      });
  };

  poll();
}
